import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faFileContract,
  faSearch,
  faFilter,
  faCoins,
  faCalendar,
  faBuilding,
  faUser,
  faCheck,
  faSort,
  faSortUp,
  faSortDown,
  faFilterCircleXmark,
  faCircleInfo,
  faMagnifyingGlassPlus,
  faPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { DealActivityModal } from '../components/deal-activity-modal/deal-activity-modal';
import { CreateOpportunityModal } from '../components/create-opportunity-modal/create-opportunity-modal';
import { PipelinesService, Deal, ListDealsParams } from '../../../core/services/pipelines.service';
import { PartnerService } from '../../../core/services/partner.service';
import { PopupInfoService } from '../../../shared/services/popup-info.service';
import { Auth } from '../../../core/auth/auth';
import { DateRangePicker } from '../../../shared/components/date-range-picker/date-range-picker';
import {
  CustomSelectComponent,
  SelectOption,
} from '../../../shared/components/custom-select/custom-select.component';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { faHandshake, faUserTag } from '@fortawesome/pro-light-svg-icons';
import { PARTNER_TYPE_OPTIONS } from '../../../core/models/partner.model';

@Component({
  selector: 'app-opportunities-page',
  imports: [
    CommonModule,
    FontAwesomeModule,
    FormsModule,
    DateRangePicker,
    CustomSelectComponent,
    DealActivityModal,
    CreateOpportunityModal,
  ],
  templateUrl: './opportunities-page.html',
  styleUrls: ['./opportunities-page.scss'],
})
export class OpportunitiesPage implements OnInit {
  private readonly pipelinesService = inject(PipelinesService);
  private readonly partnerService = inject(PartnerService);
  private readonly popupInfoService = inject(PopupInfoService);
  private readonly authService = inject(Auth);

  faFileContract = faFileContract;
  faSearch = faSearch;
  faFilter = faFilter;
  faCoins = faCoins;
  faCalendar = faCalendar;
  faBuilding = faBuilding;
  faUser = faUser;
  faCheck = faCheck;
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faFilterCircleXmark = faFilterCircleXmark;
  faCircleInfo = faCircleInfo;
  faMagnifyingGlassPlus = faMagnifyingGlassPlus;
  faPlus = faPlus;
  faUserTag = faUserTag;

  selectedDealForActivity: { id: string; name: string } | null = null;
  showCreateModal = false;

  // Filters / search
  searchTerm!: string;
  startDate!: string | null;
  endDate!: string | null;
  closeStartDate!: string | null;
  closeEndDate!: string | null;
  selectedStage = '';
  selectedPartner = '';
  selectedPartnerType = '';
  selectedCommissionStatus!: string;

  // Sort
  sortBy: ListDealsParams['sort_by'] = 'createdate';
  sortDirection: ListDealsParams['sort_direction'] = 'desc';

  deals: Deal[] = [];
  isLoading = false;
  isLoadingMore = false;
  currentPage = 1;
  perPage = 20;
  totalPages = 1;
  totalDeals = 0;

  private search$ = new Subject<string>();

  stageOptions: SelectOption[] = [
    { value: '', label: 'Todos' },
    { value: 'closedwon', label: 'Ganadas' },
    { value: 'closedlost', label: 'Perdidas' },
    { value: 'pending', label: 'En curso' },
  ];

  partnerOptions: SelectOption[] = [{ value: '', label: 'Todos los partners' }];
  partnerTypeOptions: SelectOption[] = [
    { value: '', label: 'Tipos de Partner' },
    ...PARTNER_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
  ];
  readonly faHandshake = faHandshake;

  ngOnInit(): void {
    this.search$
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe(() => this.applyFilters());

    if (this.isAdmin) {
      this.loadPartners();
    }
    this.loadDeals();
  }

  loadPartners(): void {
    this.partnerService.getPartnersWithDeals().subscribe({
      next: (res) => {
        this.partnerOptions = [
          { value: '', label: 'Todos los partners' },
          ...res.data.map((p) => ({ value: p.id.toString(), label: p.company_name })),
        ];
      },
      error: () => {
        this.partnerOptions = [{ value: '', label: 'No hay Partners...' }];
      },
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadDeals(false);
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.startDate = null;
    this.endDate = null;
    this.closeStartDate = null;
    this.closeEndDate = null;
    this.selectedStage = '';
    this.selectedPartner = '';
    this.selectedPartnerType = '';
    this.applyFilters();
  }

  get isAdmin(): boolean {
    const user = this.authService.currentUser;
    return user?.roles.includes('admin') || false;
  }

  loadDeals(append = false): void {
    if (append) {
      if (this.isLoadingMore || this.currentPage >= this.totalPages) return;
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.currentPage = 1;
    }

    const pageToLoad = append ? this.currentPage + 1 : this.currentPage;

    const params: ListDealsParams = {
      page: pageToLoad,
      per_page: this.perPage,
    };

    if (this.searchTerm) params.search = this.searchTerm;
    if (this.selectedStage) params.stage = this.selectedStage;
    if (this.selectedPartner) params.partner = this.selectedPartner;
    if (this.selectedPartnerType && this.isAdmin) params.partner_type = this.selectedPartnerType;
    if (this.startDate) params.start_date = this.startDate;
    if (this.endDate) params.end_date = this.endDate;
    if (this.closeStartDate) params.close_start_date = this.closeStartDate;
    if (this.closeEndDate) params.close_end_date = this.closeEndDate;
    if (this.selectedCommissionStatus) params.commission_status = this.selectedCommissionStatus;
    if (this.sortBy) params.sort_by = this.sortBy;
    if (this.sortDirection) params.sort_direction = this.sortDirection;

    this.pipelinesService.listDeals(params).subscribe({
      next: (response) => {
        if (append) {
          this.deals = this.deals.concat(response.data?.data || []);
          this.currentPage = pageToLoad;
          this.isLoadingMore = false;
          this.totalPages = Math.max(
            1,
            Math.ceil((response.data?.total || this.deals.length) / this.perPage)
          );
        } else {
          this.deals = response.data?.data || [];
          this.currentPage = pageToLoad;
          this.isLoading = false;
        }

        this.totalDeals = response.data?.total || this.deals.length;
        this.totalPages = response.data?.last_page || 1;
      },
      error: () => {
        const msg = 'Error al cargar las oportunidades, por favor inténtalo de nuevo más tarde.';
        this.popupInfoService.showError(msg);
        this.isLoading = false;
        this.isLoadingMore = false;
      },
    });
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el) return;
    const threshold = 20; // pixels from bottom
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      // near bottom
      this.loadDeals(true);
    }
  }

  formatCurrency(value: number): string {
    if (value === null || value === 0) return '-';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  getStageColor(stage: string): string {
    const colors: Record<string, string> = {
      prospecting: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      qualification: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      proposal: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
      negotiation: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
      closed_won: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      closed_lost: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    };
    return colors[stage] || colors['prospecting'];
  }

  getStageName(stage: string): string {
    const names: Record<string, string> = {
      prospecting: 'Prospección',
      qualification: 'Calificación',
      proposal: 'Propuesta',
      negotiation: 'Negociación',
      closed_won: 'Ganada',
      closed_lost: 'Perdida',
    };
    return names[stage] || stage;
  }

  // New: simplified badge categories per product request
  private isWon(stage: string): boolean {
    const s = (stage || '').toString().toLowerCase();
    return (
      s === 'closedwon' ||
      s === 'closed_won' ||
      s === 'closed-won' ||
      s === 'closed won' ||
      s === 'closedwon'
    );
  }

  private isLost(stage: string): boolean {
    const s = (stage || '').toString().toLowerCase();
    // include numerical legacy id '3325703400' and common variants
    return s === '3325703400' || s === 'closedlost' || s === 'closed_lost' || s === 'closed-lost';
  }

  getStageLabel(stage: string): string {
    if (this.isWon(stage)) return 'Ganada';
    if (this.isLost(stage)) return 'Perdida';
    return 'En curso';
  }

  getStageClass(stage: string): string {
    if (this.isWon(stage)) return 'stage-won';
    if (this.isLost(stage)) return 'stage-lost';
    return 'stage-ongoing';
  }

  isCommissionOk(deal: Deal): boolean {
    const status = (deal.pipeline || '').toString().toLowerCase();
    if (!status) return false;
    return ['paid', 'pagada', 'pagado', 'true', '1', 'yes', 'approved', 'ok'].includes(status);
  }

  // Derived values moved out of the template to avoid inline arrow functions
  get totalValue(): number {
    return this.deals.reduce((sum, d) => sum + (+d?.amount || 0), 0);
  }

  get closeRatePercentage(): string {
    if (!this.deals || this.deals.length === 0) return '0';
    const won = this.deals.filter((d) => d.dealstage === 'closed_won').length;
    return ((won / this.deals.length) * 100).toFixed(1);
  }

  onSearchChange(value: string) {
    this.searchTerm = value;
    this.search$.next(value || '');
  }

  onStageChange(value: string) {
    this.selectedStage = value;
    this.applyFilters();
  }

  onDateRangeChange(range: { start: string | null; end: string | null }) {
    this.startDate = range.start;
    this.endDate = range.end;
    this.applyFilters();
  }

  onCloseDateRangeChange(range: { start: string | null; end: string | null }) {
    this.closeStartDate = range.start;
    this.closeEndDate = range.end;
    this.applyFilters();
  }

  onSort(column: ListDealsParams['sort_by']): void {
    if (this.sortBy === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  getSortIcon(column: ListDealsParams['sort_by']) {
    if (this.sortBy !== column) return this.faSort;
    return this.sortDirection === 'asc' ? this.faSortUp : this.faSortDown;
  }

  get hasActiveFilters(): boolean {
    return !!(
      (this.searchTerm && this.searchTerm.trim().length > 0) ||
      this.selectedStage ||
      this.startDate ||
      this.endDate ||
      this.closeStartDate ||
      this.closeEndDate
    );
  }

  showLostReason(deal: Deal): void {
    if (!deal.closed_lost_reason) {
      this.popupInfoService.showInfo('No hay motivo de pérdida registrado para esta oportunidad.');
      return;
    }
    const message = `${deal.dealname || 'Oportunidad'}<br><br><strong>Motivo de pérdida:</strong><br>${deal.closed_lost_reason}`;
    this.popupInfoService.showInfo(message, 6000);
  }

  showActivity(deal: Deal): void {
    this.selectedDealForActivity = { id: deal.id, name: deal.dealname };
  }

  closeActivity(): void {
    this.selectedDealForActivity = null;
  }

  openCreateModal(): void {
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
  }

  onCreateSuccess(): void {
    this.showCreateModal = false;
    this.loadDeals();
  }
}
